import React, { useEffect, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";

import { CreateInvoiceModal } from "../CreateInvoiceModal";
import { CheckSalesReceiptModal } from "../CheckSalesReceiptModal";
import { OrderDetailsModal } from "../OrderDetailsModal";
import { NotesModal, NoteWithStatus } from "../NotesModal/NotesModal";
import { IAdminOrder } from "../types";
import { OrderModel } from "../../../models/OrderModel";
import { AdminModel } from "../../../models/AdminModel";
import { ConfirmDeleteOrderModal } from "../ConfirmDeleteOrderModal";
import { CustomNotificationModal } from "../CustomNotificationModal/CustomNotificationModal";
import { SentNotificationsModal } from "../SentNotificationsModal/SentNotificationsModal";
import { AdminOrderActionsModal } from "../AdminOrderActionsModal/AdminOrderActionsModal";

export interface IAdminOrderWithNotesList extends IAdminOrder {
  notesList?: NoteWithStatus[];
  startTime?: string;
  endTime?: string;
}

interface IAdminOrdersTableRowProps {
  order: IAdminOrderWithNotesList;
  reloadOrders: () => void;
}

export const AdminOrdersTableRow: React.FC<IAdminOrdersTableRowProps> = ({
  order,
  reloadOrders,
}) => {
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);
  const [isCheckSalesReceiptModalOpen, setIsCheckSalesReceiptModalOpen] = useState(false);
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const [isOrderDeleteConfirmationOpen, setIsOrderDeleteConfirmationOpen] = useState(false);
  const [isCustomNotificationOpen, setIsCustomNotificationOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [isCustomNotificationSent, setIsCustomNotificationSent] = useState(false);
  const [isSentNotificationsModalOpen, setIsSentNotificationsModalOpen] = useState(false);
  const [isConfirmationSent, setIsConfirmationSent] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isOrderActionsModalOpen, setIsOrderActionsModalOpen] = useState(false);
  const [notifyCustomerOnResponse, setNotifyCustomerOnResponse] = useState<boolean | null>(null);
  const [isSubmittingOrderResponse, setIsSubmittingOrderResponse] = useState(false);
  const history = useHistory();
  const orderStatus = order.status || "";
  const displayOrderStatus = orderStatus || "N/A";
  const displayOrderStatusUpper = displayOrderStatus.toUpperCase();
  const hasActiveTsheetsReplacement = Boolean(order.tsheetsReplacement);

  useEffect(() => {
    const hasCustomNotification = !!(
      order.adminNotifications &&
      order.adminNotifications.length > 0
    );

    setIsCustomNotificationSent(hasCustomNotification);
    setIsConfirmationSent(orderStatus === "draft");
  }, [order, orderStatus]);

  const handleOpenSentNotificationsModal = () => setIsSentNotificationsModalOpen(true);
  const handleCloseSentNotificationsModal = () => setIsSentNotificationsModalOpen(false);

  const handleOpenCustomNotification = () => setIsCustomNotificationOpen(true);
  const handleCloseCustomNotification = () => {
    setIsCustomNotificationOpen(false);
    setNotificationTitle("");
    setNotificationBody("");
  };

  const sendCustomNotification = async (title: string, body: string) => {
    try {
      const response = await fetch(`/api/notifications/send/${order._id}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, body }),
      });

      if (!response.ok) throw new Error("Failed to send custom notification");

      alert("Custom notification sent successfully");
      handleCloseCustomNotification();
      reloadOrders();
      setIsCustomNotificationSent(true);
    } catch (error) {
      alert(error);
    }
  };

  const handleCreateInvoiceClick = () => {
    // The server enforces the same rule; this guard also protects against stale or
    // programmatic clicks while the admin table is refreshing replacement state.
    if (hasActiveTsheetsReplacement) {
      return;
    }

    setIsCreateInvoiceModalOpen(true);
  };
  const handleCloseInvoiceModal = () => setIsCreateInvoiceModalOpen(false);
  const handleOpenCheckSalesReceiptModal = () => setIsCheckSalesReceiptModalOpen(true);
  const handleCloseCheckSalesReceiptModal = () => setIsCheckSalesReceiptModalOpen(false);

  const handleOrderDetailsClick = () => setIsOrderDetailsModalOpen(true);
  const handleCloseOrderDetailsModal = () => setIsOrderDetailsModalOpen(false);

  const handleOrderDeleteConfirmationClick = () => {
    // The server protects the same recovery state. This guard also covers stale clicks while
    // the row is refreshing and avoids offering an action that cannot complete safely.
    if (hasActiveTsheetsReplacement) {
      return;
    }

    setIsOrderDeleteConfirmationOpen(true);
  };
  const handleOrderDeleteConfirmationClose = () => setIsOrderDeleteConfirmationOpen(false);

  const handleOpenNotesModal = () => setIsNotesModalOpen(true);
  const handleCloseNotesModal = () => setIsNotesModalOpen(false);
  const handleCloseOrderActionsModal = () => setIsOrderActionsModalOpen(false);

  const {
    stripeInvoiceId,
    quickbooksInvoiceId,
    paymentStatus,
    paymentMethodType,
    createdAt,
    deliveryAt,
  } = order;
  // Either Stripe or QBO invoice means billing already happened for this order.
  const hasAnyInvoice = Boolean(quickbooksInvoiceId || stripeInvoiceId);
  const hasBalanceDueFollowUp = order.paymentFollowUp?.status === "balance_due";
  const hasBalanceInvoiceFollowUp =
    order.paymentFollowUp?.status === "balance_invoice_created";
  // A short check can generate a remaining-balance invoice before that invoice is actually paid,
  // so admin needs separate "sent" and "completed" states instead of one generic label.
  const hasBalanceInvoiceCreated =
    hasBalanceInvoiceFollowUp && paymentStatus === "succeeded";
  const hasOutstandingBalanceInvoice =
    hasBalanceInvoiceFollowUp && paymentStatus !== "succeeded";
  const isPaidByCheck =
    paymentStatus === "succeeded" &&
    paymentMethodType === "check" &&
    !hasBalanceDueFollowUp &&
    !hasBalanceInvoiceFollowUp;
  const needsCheckReview = order.billingReview?.status === "needs_review";

  const deleteOrder = async () => {
    handleOrderDeleteConfirmationClose();

    // A replacement may have started after the confirmation modal opened.
    if (hasActiveTsheetsReplacement) {
      return;
    }

    await OrderModel.delete(order._id);
    reloadOrders();
  };

  const handleOrderResponse = async (
    action: "confirm" | "decline" | "recreate-confirm"
  ) => {
    try {
      setIsSubmittingOrderResponse(true);
      await AdminModel.respondToOrder(
        order._id,
        action,
        Boolean(notifyCustomerOnResponse)
      );
      handleCloseOrderActionsModal();
      reloadOrders();
    } catch (error: any) {
      if (
        action === "recreate-confirm" &&
        error?.response?.status === 409 &&
        error?.response?.data?.code === "DECLINED_DELIVERY_SLOT_UNAVAILABLE"
      ) {
        // Reuse the existing delivery-time picker instead of silently assigning
        // a different customer time when the declined order's old slot has passed.
        history.push(
          `/modify-delivery/${order.tsheetsEventId}?notifyCustomer=${
            notifyCustomerOnResponse ? "1" : "0"
          }&recreateDeclined=1`
        );
        handleCloseOrderActionsModal();
        return;
      }
      const actionLabel =
        action === "recreate-confirm" ? "recreate and confirm" : action;
      const serverMessage =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message;
      alert(serverMessage || `Failed to ${actionLabel} order`);
    } finally {
      setIsSubmittingOrderResponse(false);
    }
  };

  const handleModifyOrder = () => {
    // Admin chooses notification behavior in the modal first, then we carry that choice
    // into the shared modify-delivery screen so the save step can honor it.
    history.push(
      `/modify-delivery/${order.tsheetsEventId}?notifyCustomer=${
        notifyCustomerOnResponse ? "1" : "0"
      }`
    );
    handleCloseOrderActionsModal();
  };

  const hoursBetweenCreationAndDelivery = moment(deliveryAt).diff(moment(createdAt), "hours");
  const isDeliveryToday = moment(deliveryAt).isSame(moment(), "day");
  const isLessThanOneDay = hoursBetweenCreationAndDelivery < 24 && hoursBetweenCreationAndDelivery > 0 && isDeliveryToday;

  const isDeliveryAtPastToday = moment(deliveryAt).isBefore(moment(), "day");
  const isDeclined = orderStatus === "declined";
  const isConfirmed = orderStatus === "confirmed";
  const isPast = isDeliveryAtPastToday;
  // Admins may correct confirmed orders after delivery, while other past statuses
  // keep the existing historical-order lock.
  const canManageResponse = isConfirmed || !isPast;
  const canCreateInvoice = isConfirmed && !hasActiveTsheetsReplacement;
  const getStatusButtonLabel = () => {
    if (isDeclined) {
      return "DECLINED";
    }

    if (hasActiveTsheetsReplacement) {
      return "REPLACEMENT IN PROGRESS";
    }

    if (canCreateInvoice) {
      return "Create Invoice";
    }

    if (isPast) {
      return "PAST";
    }

    return displayOrderStatusUpper;
  };
  const statusButtonLabel = getStatusButtonLabel();
  const statusButtonStyle = canCreateInvoice
    ? { backgroundColor: "#4caf50", color: "white", cursor: "pointer" }
    : { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)", cursor: "not-allowed" };

  const handleOpenOrderActionsModal = () => {
    // Keep this guard in sync with the disabled button so keyboard/programmatic
    // events cannot reopen management actions for ineligible historical orders.
    if (!canManageResponse) {
      return;
    }

    setNotifyCustomerOnResponse(null);
    setIsOrderActionsModalOpen(true);
  };

  return (
    <>
      <CreateInvoiceModal
        order={{
          ...order,
          userId: order.userId || { email: "N/A" },
        }}
        isOpen={isCreateInvoiceModalOpen && !hasActiveTsheetsReplacement}
        onClose={handleCloseInvoiceModal}
      />
      <CheckSalesReceiptModal
        order={order}
        isOpen={isCheckSalesReceiptModalOpen}
        onClose={handleCloseCheckSalesReceiptModal}
        onSubmitted={reloadOrders}
      />
      <OrderDetailsModal
        order={order}
        isOpen={isOrderDetailsModalOpen}
        onClose={handleCloseOrderDetailsModal}
      />
        <NotesModal
        isOpen={isNotesModalOpen}
        onClose={handleCloseNotesModal}
        notesList={order.notesList || [{ status: order.deliveryStatus, notes: order.deliveryNotes?.[0]?.notes || "" }]}
      />
      {!isDeclined && (
        <ConfirmDeleteOrderModal
          isOpen={isOrderDeleteConfirmationOpen}
          onCancel={handleOrderDeleteConfirmationClose}
          onClose={handleOrderDeleteConfirmationClose}
          onConfirm={deleteOrder}
        />
      )}
      <SentNotificationsModal
        isOpen={isSentNotificationsModalOpen}
        onClose={handleCloseSentNotificationsModal}
        notifications={order.adminNotifications || []}
      />
      <CustomNotificationModal
        isOpen={isCustomNotificationOpen}
        onClose={handleCloseCustomNotification}
        onSend={sendCustomNotification}
        notificationTitle={notificationTitle}
        setNotificationTitle={setNotificationTitle}
        notificationBody={notificationBody}
        setNotificationBody={setNotificationBody}
      />
      <AdminOrderActionsModal
        isOpen={isOrderActionsModalOpen}
        isSubmitting={isSubmittingOrderResponse}
        currentStatus={displayOrderStatus}
        notifyCustomer={notifyCustomerOnResponse}
        onClose={handleCloseOrderActionsModal}
        onChooseNotifyCustomer={setNotifyCustomerOnResponse}
        onConfirm={() =>
          handleOrderResponse(
            orderStatus === "declined" ? "recreate-confirm" : "confirm"
          )
        }
        onDecline={() => handleOrderResponse("decline")}
        onModify={handleModifyOrder}
      />

      <TableRow>
        <TableCell>
          {/* Short date format ("Jul 22, 2026") keeps each date column ~half the
              width of the old "Wed, July 22nd, 2026" format. */}
          {moment(createdAt).format("MMM D, YYYY")}
          {/* Always-on source tag so admins can tell at a glance who created
              the order (previously only shown for admin-created ones). */}
          <Typography
            variant="caption"
            display="block"
            style={{ color: "rgba(0, 0, 0, 0.54)" }}
          >
            {order.createdByAdmin ? "Admin-created" : "Customer-created"}
          </Typography>
        </TableCell>
        <TableCell>
          {/* Lead with the customer's real name (getAllOrders populates
              givenName/familyName); fall back to the delivery-location label
              for legacy users without name fields. */}
          {[order.userId?.givenName, order.userId?.familyName]
            .filter(Boolean)
            .join(" ") ||
            order.deliveryLocationId?.name ||
            "N/A"}
          <Typography
            variant="caption"
            display="block"
            style={{ color: "rgba(0, 0, 0, 0.54)", wordBreak: "break-word" }}
          >
            {order.userId?.email || "N/A"}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Box display="flex" flexDirection="column" alignItems="center">
            <Button size="small" color="secondary" onClick={handleOrderDetailsClick}>
              Details
            </Button>
            <Button size="small" color="secondary" onClick={handleOpenNotesModal}>
              Notes ({order.notesList?.length || 0})
            </Button>
          </Box>
        </TableCell>
        <TableCell align="center">{order.deliveryLocationId?.deliveryCost ?? "N/A"}</TableCell>
        <TableCell align="center">{moment(deliveryAt).format("MMM D, YYYY")}</TableCell>
        <TableCell align="center" style={{ padding: "8px", width: 200, minWidth: 200, maxWidth: 300 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            {isPaidByCheck ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                <Button disabled variant="contained" style={{ backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" }}>
                  Sales Receipt Created
                </Button>
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Payment: CHECK
                </Typography>
              </div>
            ) : hasBalanceDueFollowUp ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                <Button
                  onClick={handleCreateInvoiceClick}
                  disabled={hasActiveTsheetsReplacement}
                  variant="contained"
                  color="primary"
                  style={{ width: "100%" }}
                >
                  Invoice Balance
                </Button>
                {hasActiveTsheetsReplacement && (
                  <Typography variant="caption" style={{ textAlign: "center" }}>
                    QB Time replacement in progress
                  </Typography>
                )}
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Check paid: ${Number(order.paymentFollowUp?.receivedAmount || 0).toFixed(2)} / Balance due: ${Number(order.paymentFollowUp?.remainingAmount || 0).toFixed(2)}
                </Typography>
              </div>
            ) : hasOutstandingBalanceInvoice ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                <Button disabled variant="contained" style={{ backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" }}>
                  Balance Invoice Sent
                </Button>
                {/* Keep the original check amount visible here so support can quickly see what
                    was already received versus what is still expected from the customer. */}
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Check paid: ${Number(order.paymentFollowUp?.receivedAmount || 0).toFixed(2)} / Remaining invoice: ${Number(order.paymentFollowUp?.remainingAmount || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Payment: {(paymentStatus || "pending").toUpperCase()}
                </Typography>
              </div>
            ) : hasBalanceInvoiceCreated ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                <Button disabled variant="contained" style={{ backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" }}>
                  Balance Invoice Created
                </Button>
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Check paid: ${Number(order.paymentFollowUp?.receivedAmount || 0).toFixed(2)} / Remaining invoice: ${Number(order.paymentFollowUp?.remainingAmount || 0).toFixed(2)}
                </Typography>
              </div>
            ) : needsCheckReview ? (
              <Button
                onClick={handleOpenCheckSalesReceiptModal}
                variant="contained"
                color="primary"
                style={{ width: "100%" }}
              >
                Review Check
              </Button>
            ) : hasAnyInvoice ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                <Button disabled variant="contained" style={{ backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" }}>
                  Invoice Created
                </Button>
                {/* Admin role only sees payment status; payment actions stay customer-facing via email link. */}
                <Typography variant="caption" style={{ textAlign: "center" }}>
                  Payment: {(paymentStatus || "pending").toUpperCase()}
                </Typography>
              </div>
            ) : (
              <>
                <ButtonGroup variant="contained" style={{ width: "100%" }}>
                  <Button
                    onClick={handleCreateInvoiceClick}
                    disabled={!canCreateInvoice}
                    style={{
                      flexGrow: 1,
                      ...statusButtonStyle,
                    }}
                  >
                    {statusButtonLabel}
                  </Button>
                  {!isDeclined && (
                    <Button
                      onClick={handleOrderDeleteConfirmationClick}
                      disabled={hasActiveTsheetsReplacement}
                      style={{ backgroundColor: "#d32f2f", color: "white" }}
                    >
                      Delete Order
                    </Button>
                  )}
                </ButtonGroup>

                <ButtonGroup
                  variant="outlined"
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {isLessThanOneDay && (
                    <div>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleOpenCustomNotification}
                        style={{ width: "100%", borderColor: "#f50057", color: "#f50057" }}
                      >
                        Send Notification
                      </Button>
                      <Typography variant="caption" style={{ color: "#f50057", textAlign: "center", width: "100%" }}>
                        Scheduled for Delivery today!
                      </Typography>
                    </div>
                  )}
                  {isConfirmationSent && (
                    <div>
                      <Typography variant="caption" style={{ color: "#2196f3", textAlign: "center", width: "100%" }}>
                        <strong>Email Sent</strong> waiting for Confirmation
                      </Typography>
                    </div>
                  )}
                  {isCustomNotificationSent && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenSentNotificationsModal}
                      style={{ width: "100%", borderColor: "#2196f3", color: "#2196f3" }}
                    >
                      Notifications
                    </Button>
                  )}
                </ButtonGroup>
              </>
            )}
          </div>
        </TableCell>
        <TableCell align="center" style={{ minWidth: 170, width: 170 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            style={{ gap: "8px" }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenOrderActionsModal}
              disabled={!canManageResponse}
              style={{ width: "100%" }}
            >
              Manage Response
            </Button>
            <Typography
              variant="caption"
              style={{ textAlign: "center", lineHeight: 1.4 }}
            >
              Missed email or SMS
            </Typography>
            <Typography
              variant="caption"
              style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.54)" }}
            >
              Current: {displayOrderStatusUpper}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};
